/**
 * Buy Now / Add to Bag behaviour for snippets/pdp-buy-actions-snippet.liquid.
 *
 * Moved out of blocks/pdp-buy-actions.liquid so both surfaces can share it:
 * the product page renders the snippet inline, while the mini PDP injects it
 * with innerHTML — and a <script> tag added that way is never executed. A real
 * asset always runs, and the inline `onclick` attributes on the buttons keep
 * working on injected markup, so the wiring is unchanged.
 *
 * Loaded as a classic (non-module) script from layout/theme.liquid, which is
 * what keeps onBuyNow / onAddToBag global and therefore reachable from onclick.
 *
 * Every lookup is scoped to the clicked button's own .pdp-buy-actions wrapper.
 * Both surfaces can be on screen at once — open the mini PDP from a
 * recommendations row on a PDP — so a document-wide query would let the modal
 * drive the page's hidden Fastrr form and check out the wrong product.
 */

(function () {
  if (window.__pdpBuyActionsInit) return;
  window.__pdpBuyActionsInit = true;

  /* custom-variant-selector doesn't re-render the section on change — it
     dispatches `variant:change` with the matched variant, so sync from that.
     Only the PDP fires this; the mini PDP writes data-pdp-variant-id itself
     from its size-pill handler in quick-shop-simple.js. Scoped to the pdp
     context so a variant change on the page behind the modal cannot retarget
     the modal's buttons. */
  document.addEventListener('variant:change', function (e) {
    var variant = e.detail && e.detail.variant;
    if (!variant) return;
    document.querySelectorAll('.pdp-buy-actions--pdp').forEach(function (wrap) {
      wrap.dataset.pdpVariantId = variant.id;
      setPdpBuyActionsAvailable(wrap, variant.available);
    });
  });

  /* The bar's real height, for the space the page keeps clear under it
     (assets/pdp-buy-actions.css), so a taller bar cannot cover the end of
     the page. A height of 0 is skipped: the bar is hidden while a dialog or
     the category drawer is open, and dropping the space then would shorten
     the page under the shopper. */
  var bar = document.querySelector('.pdp-buy-actions--pdp');
  if (bar && window.ResizeObserver) {
    new ResizeObserver(function () {
      if (bar.offsetHeight) document.body.style.setProperty('--pdp-bar-height', bar.offsetHeight + 'px');
    }).observe(bar);
  }
})();

/* Turns the pair of buttons on or off, and has Add to Bag say why it is off
   rather than only going pale. Also called by the mini PDP's size pills
   (quick-shop-simple.js). */
function setPdpBuyActionsAvailable(wrap, available) {
  wrap.dataset.pdpAvailable = available;
  wrap.querySelectorAll('.pdp-buy-actions__btn').forEach(function (btn) {
    btn.disabled = !available;
  });
  var label = wrap.querySelector('.pdp-buy-actions__btn--add-to-bag .pdp-buy-actions__label');
  if (label) label.textContent = available ? 'Add to Bag' : 'Sold out';
}

function onAddToBag(btn) {
  var wrap = btn.closest('.pdp-buy-actions');
  if (!wrap) return;
  var vid = Number(wrap.dataset.pdpVariantId);
  if (!vid || !window.cartService) return;

  btn.disabled = true;
  btn.classList.add('is-loading');

  window.cartService
    .addItem(vid, 1)
    .then(function (response) {
      /* Same event the theme's product form fires after an add — the V2
         drawer renders itself from `sections` and opens.
         `source: 'product-form-component'` is the contract listeners key on:
         every <product-form-component> skips its own /cart.js refetch for it
         (product-form.js #onCartUpdate), and cart-icon.js adds itemCount to
         the bubble instead of overwriting it. */
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: {},
            sourceId: 'pdp-buy-actions',
            data: { source: 'product-form-component', itemCount: 1, sections: response.sections },
          },
        })
      );
    })
    .catch(function (error) {
      /* A request that never reached the store is a TypeError whose message is
         the browser's own ("Failed to fetch"), so that one gets our wording. */
      window.includToast(
        error instanceof TypeError ? 'Could not add to bag. Please try again.' : error.message,
        { type: 'error' }
      );
    })
    .finally(function () {
      btn.classList.remove('is-loading');
      btn.disabled = wrap.dataset.pdpAvailable !== 'true';
    });
}

function isIndiaTimeZone() {
  const indiaTimezones = ["Asia/Kolkata", "Asia/Calcutta"];
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return indiaTimezones.includes(userTimezone);
}

/* Same stamp the cart writes at Place Order (cartService #stampPlatform), for
   the checkouts that skip the cart. Shopify copies it onto the order. One
   object for both Buy Now paths: the /cart permalink (as a query) and Fastrr
   (as its cartAttributes). */
function platformStamp() {
  var stamp = { includ_platform: window.includIsApp ? 'APP' : 'WEB' };
  // exps / exps_seen written even when empty (with the A/B setup on the page), so an older group on
  // the cart never reaches this order.
  try {
    if (window.includExp) {
      stamp.exps = window.includExp.tag();
      stamp.exps_seen = window.includExp.seen();
    }
  } catch (e) {}
  return stamp;
}

function platformStampQuery() {
  var stamp = platformStamp();
  return '?' + Object.keys(stamp).map(function (key) {
    return 'attributes[' + key + ']=' + encodeURIComponent(stamp[key]);
  }).join('&');
}

/* Buy Now is busy from the tap until checkout shows: every Buy Now button on
   the page shows a spinner and ignores taps (styled in pdp-buy-actions.css).
   One flag for the whole page rather than the tapped button, because the size
   chart closes itself before calling onBuyNow() — the button that was tapped is
   not the one left on screen — and so a second checkout cannot be started from
   another surface meanwhile. */
var pdpBuyingTimer;

function setBuying(on) {
  clearTimeout(pdpBuyingTimer);
  document.documentElement.toggleAttribute('data-buying', on);
}

/* Coming back to this page from checkout without a reload. */
window.addEventListener('pageshow', function () {
  setBuying(false);
});

/* Leaves for checkout. This page stays up until the next one arrives, and in
   the app it stays behind the app's own checkout, so the busy state is also
   dropped after a while. */
function goToCheckout(permalink) {
  setBuying(true);
  pdpBuyingTimer = setTimeout(setBuying, 8000, false);
  window.location.href = permalink;
}

function onBuyNow(btn) {
  var wrap = btn.closest('.pdp-buy-actions');
  if (!wrap) return;
  var vid = Number(wrap.dataset.pdpVariantId);
  if (!vid || document.documentElement.hasAttribute('data-buying')) return;

  if (window.includIsApp || !isIndiaTimeZone()) {
    goToCheckout('/cart/' + vid + ':' + 1 + platformStampQuery());
    return;
  }

  initiateFastrCheckout(vid, 1, wrap);
}

var pdpFastrrTimer;

/* How long Fastrr gets to show its checkout before the shopper is sent to the
   /cart permalink instead. It was 60s with nothing on screen; the button now
   shows a spinner for the whole wait. */
var FASTRR_WAIT_MS = 15000;

/* Fastrr's own attributes, read from its cookies. Its product and cart
   checkouts both add these, and a direct checkout does not, so they are copied
   here to keep the order the same as before. */
var FASTRR_COOKIE_ATTRIBUTES = {
  EDD_check_uuid: 'edd_check_uuid',
  e360_session: 'e360_session',
  __oppref: 'oppref',
};

function fastrrCookieAttributes() {
  var attributes = {};
  document.cookie.split('; ').forEach(function (pair) {
    var i = pair.indexOf('=');
    var key = FASTRR_COOKIE_ATTRIBUTES[pair.slice(0, i)];
    if (key && pair.slice(i + 1)) attributes[key] = decodeURIComponent(pair.slice(i + 1));
  });
  return attributes;
}

/* The line Fastrr checks out, in the shape its own product checkout builds
   from /products/<handle>.js. Rejects when the variant can't be bought, so
   the caller falls back to the /cart permalink. */
var pdpProductCache = {};

function fastrrLine(productUrl, variantId, quantity) {
  var url = productUrl.split(/[?#]/)[0] + '.js';
  pdpProductCache[url] = pdpProductCache[url] || fetch(url).then(function (response) {
    if (!response.ok) throw new Error('Product request failed');
    return response.json();
  });
  pdpProductCache[url].catch(function () { delete pdpProductCache[url]; });

  return pdpProductCache[url].then(function (product) {
    var variant = product.variants.find(function (v) { return v.id === variantId; });
    if (!variant || !variant.available) throw new Error('Variant not available');
    return {
      productId: product.id,
      title: variant.name || product.title,
      variantId: variant.id,
      variantTitle: variant.public_title ? variant.title : '',
      price: parseFloat((variant.price / 100).toFixed(2)),
      quantity: quantity,
      image: variant.featured_image ? variant.featured_image.src : product.media && product.media[0] && product.media[0].src,
      item_meta_data: { properties: variant.properties },
      customAttributes: {},
      vendor: product.vendor,
      product_type: product.type,
    };
  });
}

/* Opens Fastrr's checkout for one variant with the platform stamp on the
   order. Fastrr's buyProduct can't carry it: it builds the order's attributes
   from its own cookies only. Its direct checkout takes them, so it is used
   instead. shiprocketCheckoutDirectHandler is called rather than its public
   wrapper shiprocketCheckoutEvents.InitiateDirectCheckout, because the
   wrapper reads an undeclared `data` for its analytics and throws once
   checkout has already started.

   Falls back to the /cart permalink if Fastrr is missing, throws, or its
   checkout (#fastrr-main-container) isn't showing within FASTRR_WAIT_MS.

   `wrap` is the .pdp-buy-actions the click came from; its product URL is read
   from it rather than document-wide, so the modal and the page behind it
   cannot drive each other's checkout. */
function initiateFastrCheckout(variantId, quantity = 1, wrap) {
  var permalink = '/cart/' + variantId + ':' + quantity + platformStampQuery();
  var productUrl = wrap ? wrap.dataset.pdpProductUrl : null;

  clearInterval(pdpFastrrTimer);

  function fallback() {
    clearInterval(pdpFastrrTimer);
    goToCheckout(permalink);
  }

  if (!productUrl || typeof window.shiprocketCheckoutDirectHandler !== 'function') return fallback();

  setBuying(true);

  var startedAt = Date.now();
  pdpFastrrTimer = setInterval(function () {
    var container = document.getElementById('fastrr-main-container');
    if (container && container.getClientRects().length) {
      clearInterval(pdpFastrrTimer);
      setBuying(false);
    } else if (Date.now() - startedAt >= FASTRR_WAIT_MS) {
      fallback();
    }
  }, 200);

  /* Fastrr swallows its own errors (try/catch that only logs analytics), so
     the timer above is the real fallback; this catch only covers the product
     lookup, or a throw before Fastrr's handler runs. */
  fastrrLine(productUrl, variantId, quantity)
    .then(function (line) {
      var cartAttributes = Object.assign(fastrrCookieAttributes(), platformStamp());
      window.shiprocketCheckoutDirectHandler({
        type: 'cart',
        products: [line],
        cart: [line],
        cartAttributes: cartAttributes,
        fallbackUrl: window.location.origin + permalink,
      });
    })
    .catch(fallback);
}
